import {
  onboardingIsoToDisplayDate,
  parseOnboardingDate,
} from "../../onboarding/utils/onboardingDates";
import type {
  SplitCostingPathLevel,
  SplitCostingRow,
} from "./parseSplitCostingCsv";

export const SPLIT_COSTING_PATH_FIELD_LABELS = [
  "Division",
  "Cost Centre",
  "Department",
  "Sub-department",
  "Level 6",
  "Level 7",
] as const;

export interface SplitCostingFieldResult<T> {
  value?: T;
  error?: string;
}

export interface SplitCostingPercentageValidationOptions {
  /** Legacy uploads defer non-decimal values to workflow validation. */
  allowNonNumeric?: boolean;
  /** The clustered experience retains range failures so they remain editable. */
  allowOutOfRange?: boolean;
}

export function validateSplitCostingEmployeeCode(
  raw: string
): SplitCostingFieldResult<string> {
  const value = raw.trim();
  return value
    ? { value }
    : { error: "Employee Code is required" };
}

export function validateSplitCostingPercentage(
  raw: string,
  options: SplitCostingPercentageValidationOptions = {}
): SplitCostingFieldResult<string> {
  const value = raw.trim();
  if (!value) {
    return { error: "Apportioned Percentage is required" };
  }

  const percentage = Number(value);
  if (!Number.isFinite(percentage)) {
    return options.allowNonNumeric
      ? { value }
      : { error: "Apportioned Percentage must be a valid number" };
  }
  if (
    !options.allowOutOfRange &&
    (percentage < 1 || percentage > 100)
  ) {
    return {
      error: `Apportioned Percentage must be between 1 and 100 (got ${value})`,
    };
  }
  return { value };
}

export interface SplitCostingPathValidationOptions {
  /**
   * Existing uploads only require Division at parse time; the workflow owns
   * destination resolution. The editor requires the first three path levels.
   */
  requiredLevels?: number;
}

export function validateSplitCostingPath(
  rawCodes: readonly string[],
  options: SplitCostingPathValidationOptions = {}
): SplitCostingFieldResult<SplitCostingPathLevel[]> {
  const requiredLevels = options.requiredLevels ?? 1;
  const codes = SPLIT_COSTING_PATH_FIELD_LABELS.map((_, index) =>
    (rawCodes[index] ?? "").trim()
  );

  for (let index = 0; index < requiredLevels; index += 1) {
    if (!codes[index]) {
      return {
        error: `${SPLIT_COSTING_PATH_FIELD_LABELS[index]} is required`,
      };
    }
  }

  const path: SplitCostingPathLevel[] = [];
  let foundBlank = false;
  for (let index = 0; index < codes.length; index += 1) {
    const code = codes[index];
    if (!code) {
      foundBlank = true;
      continue;
    }
    if (foundBlank) {
      return {
        error:
          `path has a gap — level ${index + 1} ` +
          `(${SPLIT_COSTING_PATH_FIELD_LABELS[index]}) is populated but a preceding level is blank`,
      };
    }
    path.push({ level: index + 1, code });
  }

  return path.length > 0
    ? { value: path }
    : { error: "Division is required" };
}

/**
 * Normalise spreadsheet dates to yyyy-MM-dd via the shared onboarding helper
 * (DD/MM/YYYY, DD-MM-YYYY, short years, YYYY-MM-DD, YYYY/MM/DD).
 * Blank input is valid and becomes null.
 */
export function normaliseSplitCostingDate(
  raw: string,
  fieldName: string
): SplitCostingFieldResult<string | null> {
  const result = parseOnboardingDate(raw, fieldName);
  if ("error" in result) return { error: result.error };
  return { value: result.value };
}

/** Convert an ISO date to the editor's DD/MM/YYYY display format. */
export function splitCostingIsoToDisplayDate(
  value: string | null
): string {
  return onboardingIsoToDisplayDate(value);
}

export interface SplitCostingClientValidationError {
  index: number;
  employeeCode: string;
  field: "apportionedPercentage";
  error: string;
}

/**
 * Range and syntax checks retained as row-backed issues by the clustered
 * upload path. Legacy parsing still blocks numeric range failures before this
 * point and continues to defer non-decimal text to the workflow.
 */
export function clientSplitCostingValidationErrors(
  rows: readonly SplitCostingRow[]
): SplitCostingClientValidationError[] {
  return rows.flatMap((row, index) => {
    const result = validateSplitCostingPercentage(
      row.apportionedPercentage
    );
    return result.error
      ? [
          {
            index,
            employeeCode: row.employeeCode,
            field: "apportionedPercentage" as const,
            error: result.error,
          },
        ]
      : [];
  });
}
